/**
 * Market-cap tiers, shared by every view that filters on them.
 *
 * Thresholds live on the server (large >= $10B, medium >= $2B, small below), derived from Polygon
 * ticker details. Two vocabularies feed in: the pulse report labels contracts mega_cap / large_cap /
 * mid_cap / small_cap, while the server tiers are large / medium / small. Mega folds into large so
 * one set of chips means the same thing everywhere; a missing or unrecognised label stays `unknown`
 * rather than being bucketed by guesswork, which is also what ETFs and indices are — they have no
 * market cap at all.
 */

export type CapTier = 'large' | 'medium' | 'small' | 'unknown';

export const CAP_TIER_OPTIONS: { value: CapTier; label: string }[] = [
  { value: 'large', label: 'Large cap' },
  { value: 'medium', label: 'Mid cap' },
  { value: 'small', label: 'Small cap' },
];

/** Accepts either vocabulary and any casing. */
export function normalizeCapTier(raw: unknown): CapTier {
  const v = String(raw ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!v) return 'unknown';
  if (v === 'mega_cap' || v === 'mega' || v === 'large_cap' || v === 'large') return 'large';
  if (v === 'mid_cap' || v === 'mid' || v === 'medium_cap' || v === 'medium') return 'medium';
  if (v === 'small_cap' || v === 'small' || v === 'micro_cap' || v === 'micro') return 'small';
  return 'unknown';
}

/** Empty selection is not a filter; `unknown` never matches a tier the user picked. */
export function matchesCapTiers(raw: unknown, selected: ReadonlySet<CapTier>): boolean {
  if (!selected.size) return true;
  return selected.has(normalizeCapTier(raw));
}
